import { ONET_ATTRIBUTION, ONET_URL, riasecMeta } from "@/lib/riasec";
import { OnetBadge } from "@/components/OnetBadge";
import { Card } from "@/components/ui/card";
import { Compass, ExternalLink, Star } from "lucide-react";

function Attribution() {
  return (
    <div className="mt-6 rounded-lg bg-slate-50 px-4 py-3" data-testid="onet-attribution">
      <OnetBadge className="mb-2" />
      <p className="text-[11px] leading-relaxed text-slate-500">
        {ONET_ATTRIBUTION}{" "}
        <a href={ONET_URL} target="_blank" rel="noreferrer" className="font-semibold text-[#005291] underline hover:text-[#78be21]">O*NET Web Services</a>.
      </p>
    </div>
  );
}

// Shared, read-only renderer for an O*NET interest result. Used by the student
// results view and the advisor read-only shared view. Never mixes with TSB.
export function InterestResults({ result }) {
  const riasec = result.riasec || [];
  const maxScore = Math.max(1, ...riasec.map((r) => r.score));
  const top3 = result.top3 || [];
  const careers = result.careers || [];

  return (
    <div className="space-y-6">
      <Card className="p-6 sm:p-8 card-elevate">
        {/* Top 3 */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Top interest areas</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="top3-areas">
            {top3.map((r, i) => {
              const m = riasecMeta(r.title);
              return (
                <div key={r.code || i} className="rounded-xl border p-4" style={{ borderColor: `${m.color}40` }}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: m.color }}>
                      {i === 0 && <Star className="h-3 w-3" />} #{i + 1}
                    </span>
                    <span className="font-mono-num text-lg font-bold" style={{ color: m.color }}>{r.score}</span>
                  </div>
                  <p className="mt-2 font-heading text-base font-bold text-[#003057]">{r.title}</p>
                  <p className="text-xs font-semibold text-slate-400">{m.tag}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{m.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 6 horizontal bars */}
        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">All six interest areas</p>
          <div className="mt-3 space-y-3" data-testid="riasec-bars">
            {riasec.map((r) => {
              const m = riasecMeta(r.title);
              const pct = Math.round((r.score / maxScore) * 100);
              return (
                <div key={r.code}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-[#003057]">{r.title} <span className="text-xs font-normal text-slate-400">· {m.tag}</span></span>
                    <span className="font-mono-num font-bold text-slate-600">{r.score}</span>
                  </div>
                  <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: m.color }} />
                  </div>
                  {r.description && <p className="mt-1 text-xs leading-relaxed text-slate-500">{r.description}</p>}
                </div>
              );
            })}
          </div>
        </div>
        <Attribution />
      </Card>

      {/* Careers to Explore */}
      <Card className="p-6 sm:p-8 card-elevate">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#003057]">
          <Compass className="h-5 w-5 text-[#78be21]" /> Careers to Explore
        </h3>
        <p className="mt-1 text-sm text-slate-500">Occupations that match this interest profile, from O*NET.</p>
        {careers.length === 0 && <p className="mt-4 text-sm text-slate-400" data-testid="no-careers">No career matches available right now.</p>}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="careers-to-explore">
          {careers.map((c) => (
            <div key={c.code} className="flex items-center justify-between rounded-xl border border-slate-200 p-4 card-elevate">
              <div>
                <p className="font-heading text-sm font-bold text-[#003057]">{c.title}</p>
                <div className="mt-1 flex items-center gap-2">
                  {c.fit && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-slate-500">{c.fit} fit</span>}
                  {c.bright_outlook && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Bright Outlook</span>}
                </div>
              </div>
              {c.code && (
                <a href={`https://www.onetonline.org/link/summary/${c.code}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#005291] hover:underline" data-testid={`career-link-${c.code}`}>
                  Details <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
